// ─── Carte d'un compte dans la liste d'administration ─────────────
// Identité à gauche (nom en gras + adresse), état à droite : demande de
// forfait en attente, forfait courant, statut.
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useUI, useT, aDemandeForfait, nomPlan } from '@mop/core';

const BADGE = { actif: '#2E7D32', en_attente: '#EF6C00', suspendu: '#C62828' };
const HIT = { top: 8, bottom: 8, left: 8, right: 8 };

// Demande de forfait : accepter/refuser SANS ouvrir la fiche — la carte porte
// déjà tout ce qu'il faut voir (plan demandé). `stopPropagation` empêche le
// tap sur le bouton de déclencher aussi `onPress` de la carte (bulle sur web).
// `onLongPress` : filet web (le swipe de `LigneSwipe`, qui enrobe cette carte
// dans `EcranComptes.js`, est natif uniquement).
export default function CarteCompte({ compte: c, onPress, onLongPress, onAccepter, onRefuser, busy, plansCfg }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const demande = aDemandeForfait(c);
  const parer = (fn) => (e) => { e?.stopPropagation?.(); if (!busy) fn?.(); };

  return (
    <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={onPress} onLongPress={onLongPress} delayLongPress={350}>
      <View style={s.avatar}><Text style={s.avatarTxt}>{(c.prenom || c.email || '?')[0].toUpperCase()}</Text></View>
      <View style={{ flex: 1 }}>
        <Text style={s.nom} numberOfLines={1}>{c.prenom || c.email || c.uid.slice(0, 6)}</Text>
        <Text style={s.mail} numberOfLines={1}>{c.email}</Text>
        {demande && (onAccepter || onRefuser) && (
          <View style={s.demandeBtns}>
            <TouchableOpacity onPress={parer(onRefuser)} disabled={busy} hitSlop={HIT}>
              <Text style={s.refuser}>{t('admin.refuser')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.accepter, busy && { opacity: 0.5 }]} onPress={parer(onAccepter)} disabled={busy} hitSlop={HIT}>
              <Text style={s.accepterTxt}>{t('admin.accepter')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      <View style={{ alignItems: 'flex-end', gap: 3 }}>
        {demande && <View style={s.demande}><Text style={s.demandeTxt}>⏳ {c.planDemande}</Text></View>}
        <View style={s.chip}><Text style={s.chipTxt}>{nomPlan(plansCfg, c.plan || 'gratuit')}</Text></View>
        <Text style={[s.statut, { color: BADGE[c.statut] || UI.muted }]}>{c.statut || 'actif'}</Text>
      </View>
    </TouchableOpacity>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  // marginBottom: 0 — l'espacement entre lignes est porté par `LigneSwipe`
  // (le wrapper), pas par la carte (cf. commentaire de tête de LigneSwipe.js).
  carte: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card,
           borderRadius: 13, borderWidth: 0.5, borderColor: UI.border, padding: 12 },
  avatar: { width: 34, height: 34, borderRadius: 17, backgroundColor: UI.accentTint,
            alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontSize: 14, fontWeight: '800', color: UI.accent },
  nom: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  mail: { fontSize: 12, color: UI.muted, marginTop: 1 },
  chip: { paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: UI.champ },
  chipTxt: { fontSize: 11.5, fontWeight: '800', color: UI.ink },
  statut: { fontSize: 11, fontWeight: '700' },
  demande: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: UI.accentTint },
  demandeTxt: { fontSize: 10.5, fontWeight: '800', color: UI.accent },
  demandeBtns: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 5 },
  refuser: { fontSize: 11.5, color: UI.muted, fontWeight: '700' },
  accepter: { backgroundColor: UI.accent, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
  accepterTxt: { color: '#fff', fontSize: 11.5, fontWeight: '800' },
});
